import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  RootState,
  eppOptionsRequest,
  webFormatCurrency,
} from '@rb-serve-app/core';
import ProgressBar from '../../components/ProgressBar';
import Breadcrumbs from '../../components/BreadCrumbs';
import Button from '../../components/Button';
import EppOptionCard from '../../components/EppOptionCard';
import moment from 'moment';
import { EventType, useAnalytics } from '../../ui/Analytics';

interface EppOption {
  optionType: string;
  numberOfPayments: string;
  optionCode: string;
  rate: string;
  description: string;
  paymentAmount: {
    value: string;
    currency: string;
  };
}

interface DynamicEppOption {
  termMonths: number;
  monthlyValue: number;
  fieldName: string;
}

const SelectEqualPaymentPlan: React.FC = () => {
  const { trackEvent } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Manual toggle for progress bar visibility
  const showProgressBar = false; // Change to true/false to show/hide progress bar

  const { transaction, accountId, customerId } = location.state || {};
  const { eppOptions, eppOptionsLoading, eppOptionsError } = useSelector(
    (state: RootState) => state.transactions
  );

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { entries, assets } = contentfulData || {};
  const { rb_transaction_history, rb_epp } = entries || {};

  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1);
  const lang = localStorage.getItem('language') || 'en-US';

  // Dynamically extract available EPP options from transaction data
  const getAvailableEppOptions = (): DynamicEppOption[] => {
    if (!transaction) return [];

    const options: DynamicEppOption[] = [];

    // Look for monthlyInstallmentValue fields and extract term numbers
    Object.keys(transaction).forEach((key) => {
      if (key.startsWith('monthlyInstallmentValue') && transaction[key]) {
        const termMatch = key.match(/monthlyInstallmentValue(\d+)/);
        if (termMatch) {
          const termMonths = parseInt(termMatch[1]);
          options.push({
            termMonths,
            monthlyValue: transaction[key],
            fieldName: key,
          });
        }
      }
    });

    // Sort by term length (shortest first)
    return options.sort((a, b) => a.termMonths - b.termMonths);
  };

  const availableEppOptions = getAvailableEppOptions();

  // Adobe tags
  useEffect(() => {
    trackEvent(
      EventType.PAGE_VIEW,
      ['select equal payment plan', 'enroll equal payment plans'],
      window.location.href
    );
  }, []);

  const trackClickEvent = (name: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      ['select equal payment plan', 'enroll equal payment plans'],
      window.location.href,
      `${name}`
    );
  };

  useEffect(() => {
    // Fetch EPP options if transaction has eppType "non0%" and activityId
    if (
      transaction?.eppType === 'non0%' &&
      transaction?.activityId &&
      accountId &&
      customerId
    ) {
      dispatch(
        eppOptionsRequest({
          accountId,
          customerId,
          activityId: transaction.activityId,
        })
      );
    }
  }, [transaction, accountId, customerId, dispatch]);

  // Reset selectedOptionIndex when transaction or available options change
  useEffect(() => {
    setSelectedOptionIndex(-1);
  }, [
    transaction?.activityId,
    availableEppOptions.length,
    availableEppOptions.map((opt) => opt.fieldName).join(','),
  ]);

  const handleCancel = () => {
    trackClickEvent('select equal payment plan|Cancel');
    navigate('/transactions', { replace: true });
  };

  const handleContinue = () => {
    trackClickEvent('select equal payment plan|Continue');
    if (selectedOptionIndex >= 0) {
      if (
        transaction.eppType === 'non0%' &&
        eppOptions?.[selectedOptionIndex]
      ) {
        // Navigate to ReviewEppPlan with selected option for non0% EPP
        navigate('/review-epp-plan', {
          state: {
            transaction: transaction,
            selectedOption: eppOptions[selectedOptionIndex],
            accountId: accountId,
            customerId: customerId,
          },
          replace: true,
        });
      } else {
        // For 0% EPP, create a selectedOption based on available options and selection
        let selectedOption;

        if (
          availableEppOptions.length > 0 &&
          availableEppOptions[selectedOptionIndex]
        ) {
          // Use the dynamic option from availableEppOptions
          const chosenOption = availableEppOptions[selectedOptionIndex];
          selectedOption = {
            numberOfPayments: chosenOption.termMonths.toString(),
            paymentAmount: {
              value: chosenOption.monthlyValue.toString(),
              currency: 'CAD',
            },
            rate: '0',
            optionType: '0%',
            optionCode: `${chosenOption.termMonths}M_0%`,
            description: `${chosenOption.termMonths} Month 0% Equal Payment Plan`,
          };
        } else {
          // Fallback to calculated values if no dynamic options are available
          selectedOption =
            selectedOptionIndex === 0
              ? {
                  numberOfPayments: '36',
                  paymentAmount: {
                    value: transaction?.amount?.value
                      ? (Number(transaction.amount.value) / 36).toFixed(2)
                      : '8.33',
                    currency: 'CAD',
                  },
                  rate: '0',
                  optionType: '0%',
                  optionCode: '36M_0%',
                  description: '36 Month 0% Equal Payment Plan',
                }
              : {
                  numberOfPayments: '48',
                  paymentAmount: {
                    value: transaction?.amount?.value
                      ? (Number(transaction.amount.value) / 48).toFixed(2)
                      : '6.25',
                    currency: 'CAD',
                  },
                  rate: '0',
                  optionType: '0%',
                  optionCode: '48M_0%',
                  description: '48 Month 0% Equal Payment Plan',
                };
        }

        navigate('/review-epp-plan', {
          state: {
            transaction: transaction,
            selectedOption: selectedOption,
            accountId: accountId,
            customerId: customerId,
          },
          replace: true,
        });
      }
    }
  };

  const stepLabels = [
    'Select Equal Payment Plan',
    'Create & Review Your Plan',
    'Enrollment Complete',
  ];

  const formatDate = (dateString: string) =>
    moment(dateString).format('MMM DD, YYYY');

  if (!transaction) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#1F1F1F]">No transaction data available</p>
      </div>
    );
  }

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      <Breadcrumbs
        items={[
          { name: 'Home', href: '/home' },
          {
            name: rb_epp?.eppWebConfig?.reviewEppPlan
              ?.transactionHistoryBreadCrumb,
            href: '/transactions',
          },
          {
            name: rb_epp?.eppWebConfig?.reviewEppPlan
              ?.equalPaymentPlanBreadCrumb,
            href: '/select-equal-payment-plan',
          },
        ]}
      />

      <div className="w-full rounded-[15px] bg-white min-h-[70vh] flex-1 flex-col overflow-y-scroll scrollbar-hide md:p-6 p-2">
        {/* Title */}
        <h1
          aria-label={rb_epp?.eppWebConfig?.selectEqualPaymentPlan?.title}
          className="text-[#1F1F1F] font-['Ted_Next'] text-[18px] sm:text-[20px] md:text-[22px] font-[650] leading-[24px] sm:leading-[26px] md:leading-[28px] tracking-[0.077px] mb-6"
        >
          {rb_epp?.eppWebConfig?.selectEqualPaymentPlan?.title}
        </h1>

        {/* Progress Bar */}
        {showProgressBar && (
          <ProgressBar
            currentStep={1}
            totalSteps={3}
            stepLabels={stepLabels}
            assets={assets}
          />
        )}

        {/* Transaction Details */}
        <div className="rounded-[10px] bg-[#FAFAFA] p-6 mb-6">
          {/* Header with merchant name and amount */}
          <div className="flex justify-between items-center mb-4">
            <h2
              className="text-[#1F1F1F] text-lg font-semibold"
              aria-label={transaction?.merchant?.name}
            >
              {transaction?.merchant?.name}
            </h2>
            <span
              className="text-[#1F1F1F] text-xl font-bold"
              aria-label={transaction.amount.value}
            >
              {transaction?.amount?.value
                ? webFormatCurrency(Number(transaction.amount.value), lang)
                : '$0.00'}
            </span>
          </div>

          {/* Separator line */}
          <div className="h-[1px] flex-shrink-0 bg-[#E9E9E9] mb-6"></div>

          {/* Transaction details in boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Transaction Date */}
            <div className="bg-white rounded-[8px] p-4 border border-[#E9E9E9]">
              <div className="flex items-center gap-3">
                {/* Calendar icon - visible on mobile (sm), hidden on tablet (md), visible on desktop (lg+) */}
                <div className="w-8 h-8 items-center justify-center flex-shrink-0 hidden sm:flex md:hidden lg:flex">
                  {assets?.icon_calendar && (
                    <img
                      src={assets.icon_calendar}
                      alt="Calendar"
                      className="w-8 h-8"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p
                    aria-label={
                      rb_epp?.eppWebConfig?.selectEqualPaymentPlan
                        ?.transactionDateLabel
                    }
                    className="text-[#1F1F1F] text-[14px] leading-[16px] mb-1"
                  >
                    {
                      rb_epp?.eppWebConfig?.selectEqualPaymentPlan
                        ?.transactionDateLabel
                    }
                  </p>
                  <p
                    aria-label={transaction.date}
                    className="text-[#666] text-[18px] font-[650] leading-[28px] tracking-[0.077px]"
                  >
                    {transaction?.date
                      ? formatDate(transaction.date)
                      : 'Jul 18, 2024'}
                  </p>
                </div>
              </div>
            </div>

            {/* Merchant Category */}
            <div className="bg-white rounded-[8px] p-4 border border-[#E9E9E9]">
              <div className="flex items-center gap-3">
                {/* Merchant category icon - visible on mobile (sm), hidden on tablet (md), visible on desktop (lg+) */}
                <div className="w-8 h-8 items-center justify-center flex-shrink-0 hidden sm:flex md:hidden lg:flex">
                  {assets?.icon_merchant && (
                    <img
                      src={assets?.icon_merchant}
                      alt="Merchant"
                      className="w-8 h-8"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p
                    aria-label={
                      rb_epp?.eppWebConfig?.selectEqualPaymentPlan
                        ?.merchantCategoryLabel
                    }
                    className="text-[#1F1F1F] text-[14px] leading-[16px] mb-1"
                  >
                    {
                      rb_epp?.eppWebConfig?.selectEqualPaymentPlan
                        ?.merchantCategoryLabel
                    }
                  </p>
                  <p
                    aria-label={transaction?.merchant?.category}
                    className="text-[#666] text-[18px] font-[650] leading-[28px] tracking-[0.077px]"
                  >
                    {transaction?.merchant?.category || 'Shopping'}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction Card Number */}
            <div className="bg-white rounded-[8px] p-4 border border-[#E9E9E9]">
              <div className="flex items-center gap-3">
                {/* Card icon - visible on mobile (sm), hidden on tablet (md), visible on desktop (lg+) */}
                <div className="w-8 h-8 items-center justify-center flex-shrink-0 hidden sm:flex md:hidden lg:flex">
                  {assets?.icon_card && (
                    <img
                      src={assets?.icon_card}
                      alt="Card"
                      className="w-8 h-8"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p
                    aria-label={
                      rb_epp?.eppWebConfig?.selectEqualPaymentPlan
                        ?.transactionCardNumberLabel
                    }
                    className="text-[#1F1F1F] text-[14px] leading-[16px] mb-1"
                  >
                    {
                      rb_epp?.eppWebConfig?.selectEqualPaymentPlan
                        ?.transactionCardNumberLabel
                    }
                  </p>
                  <p
                    aria-label={transaction?.cardNumber?.slice(-4)}
                    className="text-[#666] text-[18px] font-[650] leading-[28px] tracking-[0.077px]"
                  >
                    ****{transaction?.cardNumber?.slice(-4) || ''}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="mb-8">
          <h3
            className="text-[#1F1F1F] text-lg font-semibold mb-4"
            aria-label={
              rb_epp?.eppWebConfig?.selectEqualPaymentPlan?.selectTermLabel
            }
          >
            {rb_epp?.eppWebConfig?.selectEqualPaymentPlan?.selectTermLabel}
          </h3>

          {transaction.eppType === 'non0%' ? (
            <>
              {eppOptionsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DA291C]"></div>
                </div>
              ) : eppOptionsError ? (
                <div className="text-center py-8">
                  <p className="text-red-600">
                    Error loading payment options. Please try again.
                  </p>
                </div>
              ) : eppOptions && eppOptions.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {eppOptions.map((option: EppOption, index: number) => (
                    <EppOptionCard
                      key={index}
                      termMonths={option?.numberOfPayments || 'N/A'}
                      monthlyPaymentAmount={
                        option?.paymentAmount?.value
                          ? Number(option.paymentAmount.value)
                          : 0
                      }
                      apr={option?.rate || '0'}
                      numberOfPayments={option?.numberOfPayments || 'N/A'}
                      isSelected={selectedOptionIndex === index}
                      onSelect={() => {
                        setSelectedOptionIndex(index);
                        trackClickEvent(
                          `select equal payment plan|${option?.numberOfPayments || 'N/A'} month term`
                        );
                      }}
                      lang={lang}
                      rbWebConfig={rb_epp?.eppWebConfig?.selectEqualPaymentPlan}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#666666]">
                    No payment options available for this transaction.
                  </p>
                </div>
              )}
            </>
          ) : (
            // For 0% EPP transactions, show dynamic options based on available values

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Dynamically render EPP options based on available monthly installment values */}
              {availableEppOptions.map((option, index) => (
                <EppOptionCard
                  key={option.fieldName}
                  termMonths={option.termMonths}
                  monthlyPaymentAmount={option.monthlyValue}
                  apr="0"
                  numberOfPayments={option.termMonths}
                  isSelected={selectedOptionIndex === index}
                  onSelect={() => {
                    setSelectedOptionIndex(index);
                    trackClickEvent(
                      `select equal payment plan|${option?.termMonths || 'N/A'} month term`
                    );
                  }}
                  lang={lang}
                  rbWebConfig={rb_epp?.eppWebConfig?.selectEqualPaymentPlan}
                />
              ))}

              {/* Show message if no EPP options are available */}
              {availableEppOptions.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-[#666666]">Temporarly Unavailable</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* seperator line */}
        <div className="h-[1px] bg-[#E9E9E9] flex-shrink-0 mb-6"></div>
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <Button
            ariaLabel={rb_epp?.eppWebConfig?.common?.cancelCta}
            variant="outline"
            onClick={handleCancel}
            className="border border-[#DA291C] text-[#DA291C] px-8 py-2 hover:bg-[#DA291C] hover:text-white transition-colors"
          >
            {rb_epp?.eppWebConfig?.common?.cancelCta}
          </Button>
          <Button
            ariaLabel={rb_epp?.eppWebConfig?.common?.continueCta}
            onClick={handleContinue}
            disabled={
              selectedOptionIndex < 0 ||
              (transaction?.eppType === '0%' &&
                !availableEppOptions[selectedOptionIndex])
            }
            className={`px-8 py-2 transition-colors ${
              selectedOptionIndex >= 0 &&
              (transaction?.eppType !== '0%' ||
                availableEppOptions[selectedOptionIndex])
                ? 'bg-[#DA291C] text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {rb_epp?.eppWebConfig?.common?.continueCta}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectEqualPaymentPlan;
